import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { auditApi } from '../services/auditApi';
import { AuditCatalogue, CatalogueAction } from '../types/audit';

const STORAGE_KEY = 'garagat.admin.audit-catalogue';
const TTL_MS = 5 * 60 * 1000;

export interface CatalogueOptionGroup {
  code: string;
  label: string;
  actions: CatalogueAction[];
}

export interface AuditCatalogueValue {
  catalogue: AuditCatalogue | null;
  loading: boolean;
  error: string | null;
  /** The catalogue label of a code; unknown codes are humanised ("USER_BLOCKED" → "User blocked"). */
  labelOf: (code: string) => string;
  /** Actions grouped for the select, in catalogue group order. */
  groupedOptions: CatalogueOptionGroup[];
  entityTypes: string[];
  /** Loads (or re-loads after an error) — called by the hook, deduplicated. */
  ensureLoaded: () => void;
}

export function humanizeCode(code: string): string {
  const s = code.replace(/_/g, ' ').toLowerCase().trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : code;
}

function readCache(): AuditCatalogue | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; data: AuditCatalogue };
    if (!parsed?.data || Date.now() - parsed.at > TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data: AuditCatalogue) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* storage full or blocked: the in-memory copy still serves this page */
  }
}

export function groupCatalogue(catalogue: AuditCatalogue | null): CatalogueOptionGroup[] {
  if (!catalogue) return [];
  const byGroup = new Map<string, CatalogueAction[]>();
  catalogue.actions.forEach((a) => {
    const list = byGroup.get(a.group) ?? [];
    list.push(a);
    byGroup.set(a.group, list);
  });
  const ordered: CatalogueOptionGroup[] = catalogue.groups
    .filter((g) => byGroup.has(g.code))
    .map((g) => ({ code: g.code, label: g.label, actions: byGroup.get(g.code)! }));
  // Groups the catalogue's group list does not name (defensive) go last.
  byGroup.forEach((actions, code) => {
    if (!ordered.some((g) => g.code === code)) ordered.push({ code, label: humanizeCode(code), actions });
  });
  return ordered;
}

function buildValue(
  catalogue: AuditCatalogue | null,
  loading: boolean,
  error: string | null,
  ensureLoaded: () => void,
): AuditCatalogueValue {
  const labels = new Map((catalogue?.actions ?? []).map((a) => [a.code, a.label]));
  return {
    catalogue,
    loading,
    error,
    labelOf: (code: string) => labels.get(code) ?? labels.get(code.toUpperCase()) ?? humanizeCode(code),
    groupedOptions: groupCatalogue(catalogue),
    entityTypes: catalogue?.entityTypes ?? [],
    ensureLoaded,
  };
}

const AuditCatalogueContext = createContext<AuditCatalogueValue | null>(null);

/**
 * Spec 069 §4 — the action catalogue (`GET /api/admin/audit/catalogue`, screen audit or users), the one
 * vocabulary for the Audit screen and the admin-user tab (AC-C4). Cached in sessionStorage for 5 min.
 */
export function AuditCatalogueProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [catalogue, setCatalogue] = useState<AuditCatalogue | null>(() => readCache());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const ensureLoaded = useCallback(() => {
    if (!token || inFlight.current) return;
    if (catalogue && readCache()) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    auditApi.catalogue(token)
      .then((data) => { setCatalogue(data); writeCache(data); })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the action list'))
      .finally(() => { inFlight.current = false; setLoading(false); });
  }, [token, catalogue]);

  const value = useMemo(
    () => buildValue(catalogue, loading, error, ensureLoaded),
    [catalogue, loading, error, ensureLoaded],
  );
  return <AuditCatalogueContext.Provider value={value}>{children}</AuditCatalogueContext.Provider>;
}

const NO_PROVIDER = buildValue(null, false, null, () => {});

/** Triggers the (cached, deduplicated) load on mount. */
export function useAuditCatalogue(): AuditCatalogueValue {
  const ctx = useContext(AuditCatalogueContext);
  const value = ctx ?? NO_PROVIDER;
  const { ensureLoaded } = value;
  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);
  return value;
}
